"use client";

import { Show, SignInButton } from "@clerk/nextjs";
import { Menu } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import { Logo } from "@/components/Logo";
import { ModeToggle } from "@/components/ModeToggle";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import {
  CONTACT_URL,
  PRODUCT_GROUPS,
  PRODUCT_TONE_CLASS,
  RESOURCE_LINKS,
} from "./nav-config";

/**
 * Drawer navigation for phones and tablets, built on the shared Sheet so
 * focus trapping, Escape, and backdrop dismissal come for free.
 */
export function MobileNav() {
  const [open, setOpen] = useState(false);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="icon" aria-label="Open menu">
          <Menu className="size-5" aria-hidden />
        </Button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="w-[min(92vw,24rem)] overflow-y-auto bg-background p-0 sm:max-w-md"
      >
        <SheetHeader className="border-b border-border/50 px-5 py-4 text-left">
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <SheetDescription className="sr-only">
            Site navigation
          </SheetDescription>
          <Logo size="sm" />
        </SheetHeader>

        <nav aria-label="Mobile" className="flex flex-col gap-6 px-5 py-6">
          <div className="space-y-5">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              Products
            </p>
            {PRODUCT_GROUPS.map((group) => (
              <div
                key={group.name}
                className={cn(
                  "border-l-2 border-product pl-3",
                  PRODUCT_TONE_CLASS[group.tone],
                )}
              >
                <SheetClose asChild>
                  <Link href={group.href} className="block py-1">
                    <span className="block text-sm font-semibold text-foreground">
                      {group.name}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {group.tagline}
                    </span>
                  </Link>
                </SheetClose>
                <ul className="mt-1 space-y-0.5">
                  {group.items
                    .filter((item) => item.href !== group.href)
                    .map((item) => (
                      <li key={item.href}>
                        <SheetClose asChild>
                          <Link
                            href={item.href}
                            className="block rounded-md py-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
                          >
                            {item.label}
                          </Link>
                        </SheetClose>
                      </li>
                    ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              Resources
            </p>
            <ul className="space-y-0.5">
              {RESOURCE_LINKS.map((item) => (
                <li key={item.href}>
                  <SheetClose asChild>
                    <Link
                      href={item.href}
                      className="block rounded-md py-1.5 text-sm font-medium text-foreground transition-colors hover:text-gold"
                    >
                      {item.label}
                    </Link>
                  </SheetClose>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex flex-col gap-2 border-t border-border/50 pt-5">
            <Show when="signed-out">
              <Button variant="premium" asChild>
                <a href={CONTACT_URL} target="_blank" rel="noreferrer">
                  Request desk access
                </a>
              </Button>
              <SignInButton mode="modal">
                <Button variant="outline" onClick={() => setOpen(false)}>
                  Sign In
                </Button>
              </SignInButton>
            </Show>
            <Show when="signed-in">
              <SheetClose asChild>
                <Button variant="premium" asChild>
                  <Link href="/console">Launch App</Link>
                </Button>
              </SheetClose>
            </Show>
            <div className="flex items-center justify-between pt-2 text-sm text-muted-foreground">
              <span>Theme</span>
              <ModeToggle />
            </div>
          </div>
        </nav>
      </SheetContent>
    </Sheet>
  );
}
